import React, { useState, useMemo, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { SparkCreative, SparkFlowStep, SparkFlow, SparkJob, SparkTagDimensionSelection } from '../types/sparkTypes';
import ActiveJobCard from './ActiveJobCard';
import { JobDetailModal } from './modals/JobDetailModal';
import CreativeCard from './CreativeCard';
import { CreativeListItem } from './search/CreativeListItem';
import styles from '../styles/CreativeBoard.module.css';
import { useAtlasStore } from '../../../store/atlasStore';
import { toast } from 'react-toastify';
import { ConfirmationModal } from './modals/ConfirmationModal';
import { useSparkActions, ResolvedSparkAction } from '../hooks/useSparkActions';
import { fetchJSON } from '../../../api/fetchHelpers'; // Unified HTTP helper
import useSparkTagOptions from '../hooks/useSparkTagOptions';
import { useBoardViewState } from '../hooks/useBoardViewState';
import { sanitizePatternWithConstraints } from '../utils/tagPatterns';
import { formatActionTemplate, getJobActionLabel, getJobResultSummary } from '../utils/actionPresentation';
import { getJobResultNavigation } from '../utils/jobNavigation';
import { getJobDisplayStatus } from '../hooks/useAnimatedProgress';
import { atlasClient } from '../../../api/atlasClient';

// Material UI Icons
import SortIcon from '@mui/icons-material/Sort';
import GridViewIcon from '@mui/icons-material/GridView';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TuneIcon from '@mui/icons-material/Tune';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LabelIcon from '@mui/icons-material/Label';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ClearIcon from '@mui/icons-material/Clear';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
// Removed legacy publish/unpublish controls

// Import types from hook
import type { ViewMode, SortField, SortOrder } from '../hooks/useBoardViewState';

interface CreativeBoardProps {
  stepDefinition: SparkFlowStep;
  allCreatives: SparkCreative[];
  flowDefinition: SparkFlow;
  onCreateClick?: () => void;
  onDeleteCreatives?: (fqns: string[]) => void;
  onTriggerAction?: (action: ResolvedSparkAction, creativeFqns: string[]) => void;
  onCreativeClick?: (creativeFqn: string) => void;
  onOpenCreativeEditor?: (
    creativeFqn: string,
    iterationFqn?: string,
    initialTab?: 'details' | 'deepsights'
  ) => void;
  refreshData?: () => Promise<void>;
  dataRefreshCounter?: number; // optional counter to force re-render on data refresh
  projectTagPattern?: SparkTagDimensionSelection[] | null;
  isProjectClosed?: boolean;
  // Callbacks to notify parent of state changes for header button reactivity
  onStateChange?: (state: {
    applicableActions: ResolvedSparkAction[];
    selectedCount: number;
    hasPublishedSelected: boolean;
  }) => void;
  onNavigateToStep?: (stepIndex: number) => void;
  // Project state for empty state guidance
  hasBriefing?: boolean;
  hasResources?: boolean;
  onNavigateToBriefing?: () => void;
  onNavigateToResources?: () => void;
}

// Expose action handlers and state to parent via ref
export interface CreativeBoardHandle {
  applicableActions: ResolvedSparkAction[];
  selectedCreativeFQNs: string[];
  hasPublishedSelected: boolean;
  handleActionMenuItemClick: (action: ResolvedSparkAction) => void;
  handleDeleteClick: () => void;
}

const CreativeBoard = forwardRef<CreativeBoardHandle, CreativeBoardProps>(({
  stepDefinition,
  allCreatives,
  flowDefinition,
  onCreateClick,
  onDeleteCreatives,
  onTriggerAction,
  onCreativeClick,
  onOpenCreativeEditor,
  refreshData,
  dataRefreshCounter,
  projectTagPattern,
  isProjectClosed,
  onStateChange,
  onNavigateToStep,
  hasBriefing,
  hasResources,
  onNavigateToBriefing,
  onNavigateToResources,
}, ref) => {
  const isClosed = !!isProjectClosed;

  // Persist view state (sort, filter, view mode) across navigation
  const {
    viewMode,
    sortConfig,
    filterLabel,
    unseenOnly,
    tagFilterSelection,
    setViewMode,
    setSortConfig,
    setFilterLabel,
    setUnseenOnly,
    setTagFilterSelection,
  } = useBoardViewState(stepDefinition.creative_type_fqn);

  // Tag filter UI state (dropdown only - selection is persisted above)
  const [isTagFilterOpen, setIsTagFilterOpen] = useState<boolean>(false);
  // Filters panel expansion state
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false);

  const sparkCreativeTypes = useAtlasStore(state => state.sparkCreativeTypes);
  const currentCreativeType = useMemo(() => {
    return sparkCreativeTypes.find(ct => ct.fqn === stepDefinition.creative_type_fqn);
  }, [sparkCreativeTypes, stepDefinition.creative_type_fqn]);

  // --- Tag option hydration for upcoming tag filtering ---
  const tagDimensionFqns = useMemo(() => {
    const fqns = new Set<string>();
    projectTagPattern?.forEach((entry) => fqns.add(entry.dimension_fqn));
    currentCreativeType?.creative_tag_schema_fqn?.forEach((entry) => fqns.add(entry.dimension_fqn));
    return fqns.size > 0 ? fqns : undefined;
  }, [projectTagPattern, currentCreativeType?.creative_tag_schema_fqn]);

  const { dimensions: _tagFilterDimensions } = useSparkTagOptions(projectTagPattern, {
    dimensionFqns: tagDimensionFqns,
    schemaPattern: projectTagPattern,
  });

  const _allowedElementFqnsByDimension = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!projectTagPattern) {
      return map;
    }
    projectTagPattern.forEach((entry) => {
      map.set(entry.dimension_fqn, new Set(entry.element_fqns));
    });
    return map;
  }, [projectTagPattern]);

  // --- Sanitize current tag filter selection whenever project tag pattern changes ---
  const patternsEqual = useCallback((a: SparkTagDimensionSelection[], b: SparkTagDimensionSelection[]): boolean => {
    if (a.length !== b.length) return false;
    const sortPattern = (p: SparkTagDimensionSelection[]) =>
      [...p]
        .map((e) => ({
          dimension_fqn: e.dimension_fqn,
          element_fqns: [...e.element_fqns].sort(),
          is_optional: Boolean(e.is_optional),
          is_strict: Boolean(e.is_strict),
        }))
        .sort((x, y) => x.dimension_fqn.localeCompare(y.dimension_fqn));
    const pa = sortPattern(a);
    const pb = sortPattern(b);
    for (let i = 0; i < pa.length; i += 1) {
      if (
        pa[i].dimension_fqn !== pb[i].dimension_fqn ||
        pa[i].is_optional !== pb[i].is_optional ||
        pa[i].is_strict !== pb[i].is_strict ||
        pa[i].element_fqns.length !== pb[i].element_fqns.length
      ) {
        return false;
      }
      for (let j = 0; j < pa[i].element_fqns.length; j += 1) {
        if (pa[i].element_fqns[j] !== pb[i].element_fqns[j]) return false;
      }
    }
    return true;
  }, []);

  useEffect(() => {
    if (!projectTagPattern || tagFilterSelection.length === 0) {
      return;
    }
    const sanitized =
      sanitizePatternWithConstraints(
        tagFilterSelection,
        tagDimensionFqns,
        _allowedElementFqnsByDimension
      ) || [];
    if (!patternsEqual(tagFilterSelection, sanitized)) {
      setTagFilterSelection(sanitized);
    }
  }, [projectTagPattern, tagDimensionFqns, _allowedElementFqnsByDimension, tagFilterSelection, patternsEqual, setTagFilterSelection]);
  
  // --- Tag Filter Helpers (declared early for downstream use) ---
  const hasProjectTags = useMemo(() => Boolean(projectTagPattern && projectTagPattern.length > 0), [projectTagPattern]);
  const hasCreativeTypeTags = useMemo(() => Boolean(currentCreativeType?.creative_tag_schema_fqn && currentCreativeType.creative_tag_schema_fqn.length > 0), [currentCreativeType?.creative_tag_schema_fqn]);
  const hasTags = hasProjectTags || hasCreativeTypeTags;
  // Early-derived selection lookup for tag filter
  const _selectedElementsByDimension = useMemo(() => {
    const map = new Map<string, Set<string>>();
    (tagFilterSelection || []).forEach((entry) => {
      let set = map.get(entry.dimension_fqn);
      if (!set) {
        set = new Set<string>();
        map.set(entry.dimension_fqn, set);
      }
      entry.element_fqns.forEach((fqn) => set!.add(fqn));
    });
    return map;
  }, [tagFilterSelection]);

  // Additional state (not persisted)
  const [unseenStatuses, setUnseenStatuses] = useState<Record<string, boolean>>({});
  const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState<boolean>(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState<boolean>(false);
  const [selectedJobFqnForDetail, setSelectedJobFqnForDetail] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [locallyDismissedCompletionJobFqns, setLocallyDismissedCompletionJobFqns] = useState<Set<string>>(new Set());

  // Ref for dropdown
  const labelDropdownRef = useRef<HTMLDivElement>(null);
  const tagFilterRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const creativesGridRef = useRef<HTMLDivElement>(null);

  // Get store actions for label management
  const addSparkLabel = useAtlasStore(state => state.addSparkLabel);
  const removeSparkLabel = useAtlasStore(state => state.removeSparkLabel);
  const currentJobs = useAtlasStore(state => state.currentSparkJobs);
  const markSparkJobAsSeen = useAtlasStore(state => state.markSparkJobAsSeen);

  const resolveIterationForNavigation = useCallback(async (iterationFqn: string) => {
    try {
      return await atlasClient.getSparkIteration(iterationFqn);
    } catch {
      return null;
    }
  }, []);

  const resolveFeedbackIdForJob = useCallback(async (iterationFqn: string, jobFqn: string) => {
    try {
      const iteration = await atlasClient.getSparkIteration(iterationFqn);
      return iteration._feedback.find((feedback) => feedback._job_fqn === jobFqn)?._id || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    setLocallyDismissedCompletionJobFqns(new Set());
  }, [stepDefinition.creative_type_fqn]);

  const creativeByFqn = useMemo(() => {
    const map = new Map<string, SparkCreative>();
    allCreatives.forEach((creative) => {
      map.set(creative.fqn, creative);
    });
    return map;
  }, [allCreatives]);

  const creativeByIterationFqn = useMemo(() => {
    const map = new Map<string, SparkCreative>();
    allCreatives.forEach((creative) => {
      (creative._iteration_fqns || []).forEach((iterationFqn) => {
        map.set(iterationFqn, creative);
      });
    });
    return map;
  }, [allCreatives]);

  const isCrossStepCompletionJob = useCallback((job: SparkJob) => {
    const displayStatus = getJobDisplayStatus(job);
    return (
      displayStatus === 'completed' &&
      !job._completion_seen_by_current_user &&
      !locallyDismissedCompletionJobFqns.has(job.fqn) &&
      job._result_kind === 'creation' &&
      !!job._source_type_fqn &&
      job._source_type_fqn !== stepDefinition.creative_type_fqn
    );
  }, [locallyDismissedCompletionJobFqns, stepDefinition.creative_type_fqn]);

  // Filter jobs matching current board's creative type for active work and cross-step completion cards.
  const relevantJobs = useMemo(() => {
    const creativeTypeFqn = stepDefinition.creative_type_fqn;
    return currentJobs.filter(job => {
      const displayStatus = getJobDisplayStatus(job);

      return (
        (
          (displayStatus === 'pending' || displayStatus === 'running') &&
          (job._source_type_fqn === creativeTypeFqn || job._target_type_fqn === creativeTypeFqn)
        ) ||
        (job._target_type_fqn === creativeTypeFqn && isCrossStepCompletionJob(job))
      );
    });
  }, [currentJobs, isCrossStepCompletionJob, stepDefinition.creative_type_fqn]);

  const relevantJobEntries = useMemo(() => {
    return relevantJobs.map((job) => {
      const displayStatus = getJobDisplayStatus(job);
      const isCompletionCard = isCrossStepCompletionJob(job);

      let navigation = getJobResultNavigation(job, {
        navigateToStep: onNavigateToStep
          ? (creativeTypeFqn: string) => {
              const targetStep = flowDefinition.steps.findIndex((step) => step.creative_type_fqn === creativeTypeFqn);
              if (targetStep >= 0) {
                onNavigateToStep(targetStep + 3);
              }
            }
          : undefined,
        openCreativeEditor: onOpenCreativeEditor,
        resolveIteration: resolveIterationForNavigation,
        resolveFeedbackIdForJob,
      });

      if (isCompletionCard) {
        navigation = {
          label: 'View new items',
          action: () => {
            creativesGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
        };
      }

      return {
        job,
        navigation,
        isCompletionCard,
        title: displayStatus === 'completed' ? getJobActionLabel(job, flowDefinition) : undefined,
        summary: displayStatus === 'completed' ? getJobResultSummary(job, flowDefinition) : undefined,
      };
    });
  }, [flowDefinition, isCrossStepCompletionJob, onNavigateToStep, onOpenCreativeEditor, relevantJobs, resolveFeedbackIdForJob, resolveIterationForNavigation]);

  const hasDerivedIterationActivity = useCallback((creative: SparkCreative) => {
    return currentJobs.some(job =>
      (getJobDisplayStatus(job) === 'running' || getJobDisplayStatus(job) === 'pending') &&
      job._metadata &&
      typeof job._metadata === 'object' &&
      'iteration_fqn' in job._metadata &&
      creative._iteration_fqns?.includes(job._metadata.iteration_fqn as string)
    );
  }, [currentJobs]);

  const isCreativeProcessing = useCallback((creative: SparkCreative) => {
    return (creative._active_jobs && creative._active_jobs > 0) || hasDerivedIterationActivity(creative);
  }, [hasDerivedIterationActivity]);

  const getCreativeJobStatusTone = useCallback((creative: SparkCreative): 'running' | null => {
    return isCreativeProcessing(creative) ? 'running' : null;
  }, [isCreativeProcessing]);

  // Get all unique labels from all creatives
  const allLabels = useMemo(() => {
    const labelsSet = new Set<string>();
    allCreatives.forEach(creative => {
      creative._labels.forEach(label => labelsSet.add(label));
    });
    return Array.from(labelsSet).sort();
  }, [allCreatives]);

  // Filter creatives by step and label
  const filteredCreatives = useMemo(() => {
    // First filter by creative type
    let filtered = allCreatives.filter(
      creative => creative._creative_type_fqn === stepDefinition.creative_type_fqn
    );

    // Then filter by label if provided
    if (filterLabel.trim()) {
      filtered = filtered.filter(creative => {
        return creative._labels.some(label => 
          label.toLowerCase() === filterLabel.toLowerCase());
      });
    }

    // Then apply tag filter (AND across dimensions, OR within dimension)
    const hasTagFilter = hasTags && tagFilterSelection && tagFilterSelection.length > 0;
    if (hasTagFilter) {
      filtered = filtered.filter((creative) => {
        const pattern = creative.tag_pattern || [];
        if (pattern.length === 0) {
          return false; // selection exists; creatives without tags fail
        }
        // Build a lookup: dimension -> set of element fqns on the creative
        const creativeMap = new Map<string, Set<string>>();
        for (const entry of pattern) {
          creativeMap.set(entry.dimension_fqn, new Set(entry.element_fqns));
        }
        // For every selected dimension, require intersection >= 1
        let allDimsOk = true;
        Array.from(_selectedElementsByDimension.entries()).forEach(([dimensionFqn, selectedSet]) => {
          if (!allDimsOk || selectedSet.size === 0) return; // short-circuit or skip empty
          const creativeSet = creativeMap.get(dimensionFqn);
          if (!creativeSet) {
            allDimsOk = false; // missing dimension fails
            return;
          }
          let matched = false;
          selectedSet.forEach((el) => {
            if (creativeSet.has(el)) matched = true;
          });
          if (!matched) allDimsOk = false; // no element matched in this dimension
        });
        return allDimsOk;
      });
    }

    return filtered;
  }, [allCreatives, stepDefinition.creative_type_fqn, filterLabel, tagFilterSelection, _selectedElementsByDimension, hasTags]);

  // Edge case: if project and creative type both remove tags entirely, reset selection and close dropdown
  useEffect(() => {
    if (!hasTags && tagFilterSelection.length > 0) {
      setTagFilterSelection([]);
      setIsTagFilterOpen(false);
    }
  }, [hasTags, tagFilterSelection.length, setTagFilterSelection]);

  // Sort the filtered creatives
  const compareCreatives = useCallback((a: SparkCreative, b: SparkCreative) => {
    let comparison = 0;

    switch (sortConfig.field) {
      case 'name':
        comparison = sortConfig.order === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
        break;
      case 'createdAt': {
        const aCreatedAt = new Date(a._created_at || 0).getTime();
        const bCreatedAt = new Date(b._created_at || 0).getTime();
        comparison = sortConfig.order === 'asc'
          ? aCreatedAt - bCreatedAt
          : bCreatedAt - aCreatedAt;
        break;
      }
      case 'updatedAt': {
        const aUpdatedAt = new Date(a._updated_at || 0).getTime();
        const bUpdatedAt = new Date(b._updated_at || 0).getTime();
        comparison = sortConfig.order === 'asc'
          ? aUpdatedAt - bUpdatedAt
          : bUpdatedAt - aUpdatedAt;
        break;
      }
      default:
        comparison = 0;
    }

    if (comparison !== 0) {
      return comparison;
    }

    return a.name.localeCompare(b.name) || a.fqn.localeCompare(b.fqn);
  }, [sortConfig]);

  const sortedCreatives = useMemo(() => {
    return [...filteredCreatives].sort(compareCreatives);
  }, [compareCreatives, filteredCreatives]);

  // Re-add calculation for activeStepIndex
  const activeStepIndex = useMemo(() => {
    const setupSteps = 2; // Briefing, Insights Board
    const flowSteps = flowDefinition?.steps || [];
    const currentFlowStepIndex = flowSteps.findIndex(s => s.name === stepDefinition.name);
    return currentFlowStepIndex === -1 ? -1 : setupSteps + currentFlowStepIndex;
  }, [stepDefinition, flowDefinition]);

  // Stable empty array for useSparkActions (no multi-select; actions apply globally)
  const emptySelection = useMemo(() => [] as SparkCreative[], []);

  // Use the hook to get applicable actions
  const { actions: applicableActions } = useSparkActions(
    flowDefinition,
    activeStepIndex,
    emptySelection
  );

  // Notify parent of state changes for header button reactivity
  useEffect(() => {
    onStateChange?.({
      applicableActions,
      selectedCount: 0,
      hasPublishedSelected: false,
    });
  }, [applicableActions, onStateChange]);

  // --- Tag Filter Helpers ---
  const tagFilterActiveCount = useMemo(() => {
    return (tagFilterSelection || []).reduce((acc, entry) => acc + entry.element_fqns.length, 0);
  }, [tagFilterSelection]);

  // Total active filters count for the Filters button badge
  const totalActiveFiltersCount = useMemo(() => {
    let count = 0;
    if (tagFilterActiveCount > 0) count += 1; // Count tag filter as 1 filter
    if (filterLabel.trim()) count += 1; // Label filter
    if (unseenOnly) count += 1; // Unseen only toggle
    return count;
  }, [tagFilterActiveCount, filterLabel, unseenOnly]);

  const tagFilterDimensions = useMemo(() => {
    // Limit each dimension's elements to the ones selected on the project pattern,
    // but show all elements for creative-type-only dimensions
    return _tagFilterDimensions
      .map((dimension) => {
        const allowed = _allowedElementFqnsByDimension.get(dimension.dimensionFqn);
        if (allowed) {
          // Project-level dimension: restrict to allowed elements
          return { ...dimension, elements: dimension.elements.filter((el) => allowed.has(el.fqn)) };
        }
        // Creative-type-only dimension: show all elements
        return dimension;
      })
      .filter((d) => d.elements.length > 0);
  }, [_tagFilterDimensions, _allowedElementFqnsByDimension]);

  const handleTagFilterDropdownToggle = useCallback(() => {
    if (!hasTags) return;
    setIsTagFilterOpen((prev) => !prev);
  }, [hasTags]);

  const handleClearTagFilter = useCallback(() => {
    setTagFilterSelection([]);
    setIsTagFilterOpen(false);
  }, [setTagFilterSelection]);

  const handleToggleTagFilterElement = useCallback((dimensionFqn: string, elementFqn: string) => {
    const allowed = _allowedElementFqnsByDimension.get(dimensionFqn);
    if (allowed && !allowed.has(elementFqn)) {
      return; // guard against stale clicks
    }
    const projectEntry = projectTagPattern?.find((e) => e.dimension_fqn === dimensionFqn);
    setTagFilterSelection((prev) => {
      const current = prev.find((e) => e.dimension_fqn === dimensionFqn);
      const currentElements = current?.element_fqns ?? [];
      const isSelected = currentElements.includes(elementFqn);
      const nextElements = isSelected
        ? currentElements.filter((f) => f !== elementFqn)
        : [...currentElements, elementFqn];
      const nextEntry: SparkTagDimensionSelection = {
        dimension_fqn: dimensionFqn,
        element_fqns: nextElements,
        is_optional: current?.is_optional ?? (projectEntry?.is_optional ?? false),
        is_strict: current?.is_strict ?? (projectEntry?.is_strict ?? false),
      };
      const next = prev.filter((e) => e.dimension_fqn !== dimensionFqn);
      if (nextElements.length > 0) {
        next.push(nextEntry);
      }
      return next;
    });
  }, [_allowedElementFqnsByDimension, projectTagPattern, setTagFilterSelection]);

  // Toggle view mode between small and large
  const handleViewModeToggle = (mode: ViewMode) => setViewMode(mode);

  // Handle filter dropdown toggle
  const handleFilterDropdownToggle = () => {
    setIsLabelDropdownOpen(prev => !prev);
  };

  // Handle filter selection (when no creatives selected)
  const handleFilterSelect = (label: string) => {
    setFilterLabel(label === filterLabel ? '' : label);
    setIsLabelDropdownOpen(false);
  };

  // Handle click on a creative card (always open editor)
  const handleCreativeClick = (fqn: string) => {
    if (onCreativeClick) {
      onCreativeClick(fqn);
    }
  };

  // Label management handlers for inline card label picker
  const handleCardAddLabel = useCallback(async (creativeFqn: string, label: string) => {
    try {
      await addSparkLabel([creativeFqn], label);
      if (refreshData) await refreshData();
    } catch (error) {
      console.error('Failed to add label:', error);
      toast.error('Failed to add label.');
    }
  }, [addSparkLabel, refreshData]);

  const handleCardRemoveLabel = useCallback(async (creativeFqn: string, label: string) => {
    try {
      await removeSparkLabel([creativeFqn], label);
      if (refreshData) await refreshData();
    } catch (error) {
      console.error('Failed to remove label:', error);
      toast.error('Failed to remove label.');
    }
  }, [removeSparkLabel, refreshData]);

  // Add state for label deletion confirmation modal
  const [isDeleteLabelModalOpen, setIsDeleteLabelModalOpen] = useState<boolean>(false);
  const [labelToDelete, setLabelToDelete] = useState<string>('');
  const [isDeletingLabel, setIsDeletingLabel] = useState<boolean>(false);

  // Handle confirmation for label deletion
  const handleDeleteLabelConfirm = async () => {
    if (!labelToDelete) return;

    try {
      setIsDeletingLabel(true);

      // Get all creatives with this label
      const creativesWithLabel = allCreatives.filter(
        creative => creative._labels.includes(labelToDelete)
      );

      if (creativesWithLabel.length > 0) {
        const fqns = creativesWithLabel.map(c => c.fqn);
        await removeSparkLabel(fqns, labelToDelete);
      }

      toast.success(`Deleted label "${labelToDelete}"`);

      // Clear filter if the deleted label was being used
      if (filterLabel === labelToDelete) {
        setFilterLabel('');
      }

      // Refresh data after successful operation
      if (refreshData) {
        await refreshData();
      }
    } catch (error) {
      console.error('Failed to delete label:', error);
      toast.error('Failed to delete label. Please try again.');
    } finally {
      setIsDeletingLabel(false);
      setIsDeleteLabelModalOpen(false);
      setLabelToDelete('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(event.target as Node)) {
        setIsLabelDropdownOpen(false);
      }
      if (tagFilterRef.current && !tagFilterRef.current.contains(event.target as Node)) {
        setIsTagFilterOpen(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle clearing the filter
  const handleClearFilter = () => {
    setFilterLabel('');
    setIsLabelDropdownOpen(false);
  };

  // Fetch unseen statuses for all creatives when board (creative type) or creatives list changes
  useEffect(() => {
    if (sortedCreatives.length === 0) {
      setUnseenStatuses({});
      return;
    }
    // derive project FQN from first creative and fetch unseen list
    const projectFqn = sortedCreatives[0]._project_fqn;
    (async () => {
      try {
        const unseenCreativeFqns = await fetchJSON<string[]>(
          `/api/spark/creatives/project/${projectFqn}/creative_type/${stepDefinition.creative_type_fqn}/unseen`
        );
        const statuses: Record<string, boolean> = {};
        sortedCreatives.forEach(c => {
          statuses[c.fqn] = unseenCreativeFqns.includes(c.fqn);
        });
        setUnseenStatuses(statuses);
      } catch (error) {
        console.error('Failed fetching unseen statuses', error);
      }
    })();
  }, [sortedCreatives, stepDefinition.creative_type_fqn]);

  // Determine visible creatives based on unseenOnly filter and parsed statuses
  const visibleCreatives = useMemo(() => {
    let list = [...sortedCreatives];
    if (unseenOnly) {
      list = list.filter(c => unseenStatuses[c.fqn]);
    }
    return list;
  }, [sortedCreatives, unseenOnly, unseenStatuses]);

  const visibleCreativeFqns = useMemo(() => {
    return new Set(visibleCreatives.map((creative) => creative.fqn));
  }, [visibleCreatives]);

  // Determine potential ancestor types from flow definition (derive actions that output to this board's type)
  const potentialAncestorTypeFqns = useMemo(() => {
    if (!flowDefinition?.steps) return [];
    const currentTypeFqn = stepDefinition.creative_type_fqn;
    const typeFqns = new Set<string>();
    flowDefinition.steps.forEach(step => {
      if (step.creative_type_fqn === currentTypeFqn) return;
      (step.derive || []).forEach(ref => {
        if (ref.output_type_fqn === currentTypeFqn) {
          typeFqns.add(step.creative_type_fqn);
        }
      });
    });
    return Array.from(typeFqns);
  }, [flowDefinition, stepDefinition.creative_type_fqn]);

  const hasPotentialAncestors = potentialAncestorTypeFqns.length > 0;

  // Build map of all potential ancestor creatives from allCreatives (no API call needed)
  const potentialAncestorCreatives = useMemo(() => {
    if (potentialAncestorTypeFqns.length === 0) return {};
    const typeFqnSet = new Set(potentialAncestorTypeFqns);
    const map: Record<string, SparkCreative> = {};
    allCreatives.forEach(creative => {
      if (typeFqnSet.has(creative._creative_type_fqn)) {
        map[creative.fqn] = creative;
      }
    });
    return map;
  }, [allCreatives, potentialAncestorTypeFqns]);

  // Resolve derive actions per ancestor type (for triggering from empty group placeholders)
  const ancestorDeriveActions = useMemo((): Record<string, ResolvedSparkAction> => {
    if (!flowDefinition?.steps || !flowDefinition.actions || !flowDefinition.creative_types) return {};
    const currentTypeFqn = stepDefinition.creative_type_fqn;
    const result: Record<string, ResolvedSparkAction> = {};

    flowDefinition.steps.forEach(step => {
      if (step.creative_type_fqn === currentTypeFqn) return;
      (step.derive || []).forEach(ref => {
        if (ref.output_type_fqn === currentTypeFqn && !result[step.creative_type_fqn]) {
          const actionDef = flowDefinition.actions.find(a => a.name.toLowerCase() === ref.name.toLowerCase());
          if (actionDef) {
            const inputTypeFqn = ref.input_type_fqn ?? step.creative_type_fqn;
            const outputTypeFqn = ref.output_type_fqn;
            const label = formatActionTemplate(
              actionDef.label,
              inputTypeFqn,
              outputTypeFqn,
              flowDefinition.creative_types
            );
            const desc = formatActionTemplate(
              actionDef.description,
              inputTypeFqn,
              outputTypeFqn,
              flowDefinition.creative_types
            );

            result[step.creative_type_fqn] = {
              ...actionDef,
              category: 'derive',
              input_type_fqn: inputTypeFqn,
              output_type_fqn: outputTypeFqn,
              formatted_label: label || 'Action',
              formatted_description: desc || 'Action',
              logo_url: actionDef.logo_url,
            };
          }
        }
      });
    });
    return result;
  }, [flowDefinition, stepDefinition.creative_type_fqn]);

  // Group creatives by potential ancestors
  const groupedCreatives = useMemo(() => {
    if (!hasPotentialAncestors) {
      return { ungrouped: visibleCreatives };
    }

    const groups: Record<string, SparkCreative[]> = { ungrouped: [] };

    // Pre-seed all potential ancestor groups (even if empty)
    Object.keys(potentialAncestorCreatives).forEach(fqn => {
      groups[fqn] = [];
    });

    // Distribute child creatives
    visibleCreatives.forEach(creative => {
      if (creative.ancestor_creative_fqns?.length) {
        creative.ancestor_creative_fqns.forEach(ancestorFqn => {
          if (groups[ancestorFqn]) {
            groups[ancestorFqn].push(creative);
          } else {
            groups.ungrouped.push(creative);
          }
        });
      } else {
        groups.ungrouped.push(creative);
      }
    });

    return groups;
  }, [visibleCreatives, hasPotentialAncestors, potentialAncestorCreatives]);

  const sortedAncestorGroups = useMemo(() => {
    if (!hasPotentialAncestors) {
      return [];
    }

    return Object.values(potentialAncestorCreatives)
      .sort(compareCreatives)
      .map((ancestor) => ({
        ancestor,
        creatives: groupedCreatives[ancestor.fqn] || [],
      }));
  }, [compareCreatives, groupedCreatives, hasPotentialAncestors, potentialAncestorCreatives]);

  const autoRevealCompletionJobs = useMemo(() => {
    return relevantJobs
      .filter((job) => isCrossStepCompletionJob(job))
      .map((job) => {
        const actionArgs = job._action_args?.action_args ?? {};
        const inputIterationFqns = new Set<string>();
        if (typeof actionArgs.input_iteration_fqn === 'string') {
          inputIterationFqns.add(actionArgs.input_iteration_fqn);
        }
        if (Array.isArray(actionArgs.input_iteration_fqns)) {
          actionArgs.input_iteration_fqns.forEach((iterationFqn) => {
            if (typeof iterationFqn === 'string') {
              inputIterationFqns.add(iterationFqn);
            }
          });
        }

        const sourceAncestorCreativeFqns = new Set<string>();
        inputIterationFqns.forEach((iterationFqn) => {
          const ancestorCreative = creativeByIterationFqn.get(iterationFqn);
          if (ancestorCreative) {
            sourceAncestorCreativeFqns.add(ancestorCreative.fqn);
          }
        });

        const createdCreativesByFqn = new Map<string, SparkCreative>();
        (job._result?.saved_fqns || []).forEach((savedFqn) => {
          const creative = creativeByFqn.get(savedFqn) || creativeByIterationFqn.get(savedFqn);
          if (creative && creative._creative_type_fqn === stepDefinition.creative_type_fqn) {
            createdCreativesByFqn.set(creative.fqn, creative);
          }
        });

        if (createdCreativesByFqn.size === 0 && sourceAncestorCreativeFqns.size > 0) {
          const jobCreatedAtMs = new Date(job._created_at).getTime();
          visibleCreatives.forEach((creative) => {
            if (creative._creative_type_fqn !== stepDefinition.creative_type_fqn) {
              return;
            }

            const hasMatchingAncestor = (creative.ancestor_creative_fqns || []).some((ancestorFqn) =>
              sourceAncestorCreativeFqns.has(ancestorFqn)
            );
            if (!hasMatchingAncestor) {
              return;
            }

            const createdAtMs = creative._created_at ? new Date(creative._created_at).getTime() : 0;
            const changedAtMs = creative._changed_at ? new Date(creative._changed_at).getTime() : createdAtMs;
            if (Math.max(createdAtMs, changedAtMs) >= jobCreatedAtMs) {
              createdCreativesByFqn.set(creative.fqn, creative);
            }
          });
        }

        const createdCreatives = Array.from(createdCreativesByFqn.values());
        const visibleCreatedCreatives = createdCreatives.filter((creative) => visibleCreativeFqns.has(creative.fqn));
        const ancestorCreativeFqns = new Set<string>();

        visibleCreatedCreatives.forEach((creative) => {
          (creative.ancestor_creative_fqns || []).forEach((ancestorFqn) => {
            if (potentialAncestorCreatives[ancestorFqn]) {
              ancestorCreativeFqns.add(ancestorFqn);
            }
          });
        });

        sourceAncestorCreativeFqns.forEach((ancestorFqn) => {
          if (potentialAncestorCreatives[ancestorFqn]) {
            ancestorCreativeFqns.add(ancestorFqn);
          }
        });

        return {
          job,
          visibleCreatedCreatives,
          ancestorCreativeFqns,
        };
      })
      .filter(({ visibleCreatedCreatives }) => visibleCreatedCreatives.length > 0);
  }, [
    creativeByFqn,
    creativeByIterationFqn,
    isCrossStepCompletionJob,
    potentialAncestorCreatives,
    relevantJobs,
    stepDefinition.creative_type_fqn,
    visibleCreatives,
    visibleCreativeFqns,
  ]);

  useEffect(() => {
    const pendingAutoRevealJobs = autoRevealCompletionJobs.filter(
      ({ job }) => !locallyDismissedCompletionJobFqns.has(job.fqn)
    );

    if (pendingAutoRevealJobs.length === 0) {
      return;
    }

    setLocallyDismissedCompletionJobFqns((prev) => {
      const next = new Set(prev);
      pendingAutoRevealJobs.forEach(({ job }) => {
        next.add(job.fqn);
      });
      return next;
    });

    const ancestorCreativeFqnsToReveal = new Set<string>();
    pendingAutoRevealJobs.forEach(({ ancestorCreativeFqns }) => {
      ancestorCreativeFqns.forEach((ancestorFqn) => ancestorCreativeFqnsToReveal.add(ancestorFqn));
    });

    if (ancestorCreativeFqnsToReveal.size > 0) {
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        let changed = false;
        ancestorCreativeFqnsToReveal.forEach((ancestorFqn) => {
          if (next.delete(ancestorFqn)) {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }

    const jobFqns = pendingAutoRevealJobs.map(({ job }) => job.fqn);

    void (async () => {
      await markSparkJobAsSeen(jobFqns);
    })();
  }, [autoRevealCompletionJobs, locallyDismissedCompletionJobFqns, markSparkJobAsSeen]);

  // Find actions from other steps that can derive/generate this creative type
  const sourceStepHints = useMemo(() => {
    if (!flowDefinition?.steps || !flowDefinition.actions) return [];

    const currentTypeFqn = stepDefinition.creative_type_fqn;
    const hints: { stepIndex: number; stepName: string; sourceTypeName: string; actionLabel: string }[] = [];

    flowDefinition.steps.forEach((step, index) => {
      // Skip current step
      if (step.creative_type_fqn === currentTypeFqn) return;

      // Check derive actions that output to our type
      const deriveActions = step.derive || [];
      deriveActions.forEach((ref) => {
        if (ref.output_type_fqn === currentTypeFqn) {
          const actionDef = flowDefinition.actions.find(a => a.name.toLowerCase() === ref.name.toLowerCase());
          const sourceType = sparkCreativeTypes.find(ct => ct.fqn === step.creative_type_fqn);
          if (actionDef && sourceType) {
            hints.push({
              stepIndex: index + 3, // +3 because steps 0-2 are Setup, Briefing, Resources
              stepName: step.name,
              sourceTypeName: sourceType.name,
              actionLabel: formatActionTemplate(
                actionDef.label,
                ref.input_type_fqn ?? step.creative_type_fqn,
                ref.output_type_fqn,
                flowDefinition.creative_types
              ).toLowerCase(),
            });
          }
        }
      });
    });

    return hints;
  }, [flowDefinition, stepDefinition.creative_type_fqn, sparkCreativeTypes]);

  // Render empty state icon in circle
  const renderEmptyStateIcon = () => (
    <div className={styles.emptyStateIcon}>
      {currentCreativeType?.icon_url ? (
        <img src={currentCreativeType.icon_url} alt="" className={styles.emptyStateIconImage} />
      ) : (
        <LightbulbOutlinedIcon />
      )}
    </div>
  );

  // Render creative cards with grouping
  const renderCreativeCards = () => {
    // Skip full empty state when ancestor creatives exist — let grouped rendering show them
    const hasAncestorCreativesToDisplay = hasPotentialAncestors && Object.keys(potentialAncestorCreatives).length > 0;

    if (sortedCreatives.length === 0 && !hasAncestorCreativesToDisplay) {
      const pluralName = currentCreativeType?.plural_name?.toLowerCase() || 'content';
      const inventActions = applicableActions.filter(a => a.category === 'invent');

      // Case 0: No briefing - prompt to create briefing first
      if (!hasBriefing && onNavigateToBriefing) {
        return (
          <div className={styles.emptyState}>
            {renderEmptyStateIcon()}
            <h3 className={styles.emptyStateTitle}>Your {stepDefinition.name} is empty</h3>
            <p className={styles.emptyStateDescription}>
              Complete your project briefing to guide DeepSights in generating {pluralName} for your project.
            </p>
            <div className={styles.emptyStateActions}>
              <button
                type="button"
                className={styles.emptyStatePrimaryButton}
                onClick={onNavigateToBriefing}
                disabled={isClosed}
              >
                Complete your briefing
              </button>
            </div>
          </div>
        );
      }

      // Case C: Has invent actions - show AI button (disabled when no resources)
      if (inventActions.length > 0) {
        const primaryAction = inventActions[0];
        const understandingLabel = flowDefinition.understanding_label || 'Understanding';
        return (
          <div className={styles.emptyState}>
            {renderEmptyStateIcon()}
            <h3 className={styles.emptyStateTitle}>Your {stepDefinition.name} is empty</h3>
            <p className={styles.emptyStateDescription}>
              {!hasResources
                ? `Add resources in ${understandingLabel} before generating ${pluralName}.`
                : `Use DeepSights to ${primaryAction.formatted_label.toLowerCase()} for your project.`
              }
            </p>
            <div className={styles.emptyStateActions}>
              {!hasResources && onNavigateToResources ? (
                <button
                  type="button"
                  className={styles.emptyStatePrimaryButton}
                  onClick={onNavigateToResources}
                  disabled={isClosed}
                >
                  Go to {understandingLabel}
                </button>
              ) : (
                <>
                  {sourceStepHints.length > 0 && (
                    <button
                      type="button"
                      className={styles.emptyStatePrimaryButton}
                      onClick={() => onNavigateToStep?.(sourceStepHints[0].stepIndex)}
                      disabled={isClosed}
                    >
                      Go to {sourceStepHints[0].stepName}
                    </button>
                  )}
                  <button
                    type="button"
                    className={`${styles.emptyStatePrimaryButton} ${styles.aiButton}`}
                    onClick={() => onTriggerAction?.(primaryAction, [])}
                    disabled={isClosed}
                  >
                    <AutoAwesomeIcon fontSize="small" />
                    {primaryAction.formatted_label}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      }

      // Case A: No resources and no invent actions - prompt to add resources
      if (!hasResources && onNavigateToResources) {
        const understandingLabel = flowDefinition.understanding_label || 'Understanding';
        const understandingDesc = flowDefinition.understanding_description || 'Add knowledge and insights';
        return (
          <div className={styles.emptyState}>
            {renderEmptyStateIcon()}
            <h3 className={styles.emptyStateTitle}>Your {stepDefinition.name} is empty</h3>
            <p className={styles.emptyStateDescription}>
              {understandingDesc} to inform the creation of {pluralName} for your project.
            </p>
            <div className={styles.emptyStateActions}>
              <button
                type="button"
                className={styles.emptyStatePrimaryButton}
                onClick={onNavigateToResources}
                disabled={isClosed}
              >
                Go to {understandingLabel}
              </button>
            </div>
          </div>
        );
      }

      // Case B: No invent actions, but has source step hints - navigate to source step
      if (sourceStepHints.length > 0) {
        const hint = sourceStepHints[0];
        return (
          <div className={styles.emptyState}>
            {renderEmptyStateIcon()}
            <h3 className={styles.emptyStateTitle}>Your {stepDefinition.name} is empty</h3>
            <p className={styles.emptyStateDescription}>
              Open a {hint.sourceTypeName.toLowerCase()} in the {hint.stepName} board and use an agent to generate {pluralName} from there.
            </p>
            <div className={styles.emptyStateActions}>
              <button
                type="button"
                className={styles.emptyStatePrimaryButton}
                onClick={() => onNavigateToStep?.(hint.stepIndex)}
                disabled={isClosed}
              >
                Go to {hint.stepName}
              </button>
            </div>
          </div>
        );
      }

      // Fallback: No actions available
      return (
        <div className={styles.emptyState}>
          {renderEmptyStateIcon()}
          <h3 className={styles.emptyStateTitle}>Your {stepDefinition.name} is empty</h3>
          <p className={styles.emptyStateDescription}>
            {onCreateClick
              ? `Create a new ${currentCreativeType?.name?.toLowerCase() || 'item'} to get started.`
              : 'No actions available yet.'}
          </p>
          {onCreateClick && (
            <div className={styles.emptyStateActions}>
              <button
                type="button"
                className={styles.emptyStatePrimaryButton}
                onClick={onCreateClick}
                disabled={isClosed}
              >
                Create manually
              </button>
            </div>
          )}
        </div>
      );
    }

    const displayCreatives = unseenOnly
      ? sortedCreatives.filter(c => unseenStatuses[c.fqn])
      : sortedCreatives;

    if (displayCreatives.length === 0 && !hasAncestorCreativesToDisplay) {
      return (
        <div className={styles.emptyState}>
          {renderEmptyStateIcon()}
          <h3 className={styles.emptyStateTitle}>
            Nothing unseen here
          </h3>
          <p className={styles.emptyStateDescription}>
            Toggle off Unseen filter to view all cards
          </p>
        </div>
      );
    }

    if (!hasPotentialAncestors) {
      if (viewMode === 'list') {
        return (
          <div className={styles.listContainer}>
            {displayCreatives.map((creative) => (
              <CreativeListItem
                key={creative.fqn}
                fqn={creative.fqn}
                title={creative.name}
                description={creative.description || ''}
                thumbnailUrl={creative._hero_image_id ? atlasClient.getAssetUrl(creative._hero_image_id) : undefined}
                tagPattern={(creative as any).tag_pattern || []}
                labels={creative._labels || []}
                isPublished={creative.is_published}
                onClick={() => handleCreativeClick(creative.fqn)}
              />
            ))}
          </div>
        );
      }
      return (
        <ul className={`${styles.creativesContainer} ${styles.largeGrid}`}>
          {displayCreatives.map(creative => (
              <li key={creative.fqn} className={styles.cardWrapper}>
                <CreativeCard
                  creative={creative}
                  size="large"
                  onClick={() => handleCreativeClick(creative.fqn)}
                  onDelete={onDeleteCreatives && !isClosed && !(creative as any).is_published ?
                    (fqn) => {
                      onDeleteCreatives([fqn]);
                    } : undefined}
                  thumbnailUrl={undefined}
                  activeIterationNumber={creative._iteration_fqns?.length}
                  isProcessing={isCreativeProcessing(creative)}
                  jobStatusTone={getCreativeJobStatusTone(creative)}
                  hasUnseen={unseenStatuses[creative.fqn]}
                  heroRefreshCounter={dataRefreshCounter}
                  availableLabels={allLabels}
                  onAddLabel={!isClosed ? handleCardAddLabel : undefined}
                  onRemoveLabel={!isClosed ? handleCardRemoveLabel : undefined}
                  isReadOnly={isClosed}
                />
              </li>
          ))}
        </ul>
      );
    }

    // Render grouped cards
    return (
      <div className={styles.groupedContainer}>
        {/* Render ungrouped section first if it has items */}
        {groupedCreatives.ungrouped.length > 0 && (
          <div className={styles.creativeGroup}>
            <h3 className={styles.groupTitle}>Fresh Creations</h3>
            {viewMode === 'list' ? (
              <div className={styles.listContainer}>
                {groupedCreatives.ungrouped.map((creative) => (
                  <CreativeListItem
                    key={creative.fqn}
                    fqn={creative.fqn}
                    title={creative.name}
                    description={creative.description || ''}
                    thumbnailUrl={creative._hero_image_id ? atlasClient.getAssetUrl(creative._hero_image_id) : undefined}
                    tagPattern={(creative as any).tag_pattern || []}
                    isPublished={creative.is_published}
                    onClick={() => handleCreativeClick(creative.fqn)}
                  />
                ))}
              </div>
            ) : (
              <ul className={`${styles.creativesContainer} ${styles.largeGrid}`}>
                {groupedCreatives.ungrouped.map(creative => (
                  <li key={creative.fqn} className={styles.cardWrapper}>
                    <CreativeCard
                      creative={creative}
                      size="large"
                      onClick={() => handleCreativeClick(creative.fqn)}
                      onDelete={onDeleteCreatives && !isClosed && !(creative as any).is_published ?
                        (fqn) => {
                          onDeleteCreatives([fqn]);
                        } : undefined}
                      thumbnailUrl={undefined}
                      activeIterationNumber={creative._iteration_fqns?.length}
                      isProcessing={isCreativeProcessing(creative)}
                      jobStatusTone={getCreativeJobStatusTone(creative)}
                      hasUnseen={unseenStatuses[creative.fqn]}
                      heroRefreshCounter={dataRefreshCounter}
                      availableLabels={allLabels}
                      onAddLabel={!isClosed ? handleCardAddLabel : undefined}
                      onRemoveLabel={!isClosed ? handleCardRemoveLabel : undefined}
                      isReadOnly={isClosed}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Render potential ancestor groups */}
        {sortedAncestorGroups.map(({ ancestor, creatives }) => {
          const ancestorFqn = ancestor.fqn;

          return (
            <div key={ancestorFqn} className={styles.creativeGroup}>
              <div
                className={styles.groupHeader}
                onClick={() => onCreativeClick?.(ancestorFqn)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onCreativeClick?.(ancestorFqn); }}
              >
                <button
                  type="button"
                  className={`${styles.groupCollapseToggle} ${collapsedGroups.has(ancestorFqn) ? styles.collapsed : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCollapsedGroups(prev => {
                      const next = new Set(prev);
                      if (next.has(ancestorFqn)) next.delete(ancestorFqn);
                      else next.add(ancestorFqn);
                      return next;
                    });
                  }}
                  aria-label={collapsedGroups.has(ancestorFqn) ? 'Expand group' : 'Collapse group'}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </button>
                <div className={styles.groupHeaderContent}>
                  <span className={styles.groupHeaderTitle}>{ancestor.name}</span>
                  {ancestor.description && (
                    <span className={styles.groupHeaderDesc}>{ancestor.description}</span>
                  )}
                  <span className={styles.groupHeaderType}>
                    {sparkCreativeTypes.find(ct => ct.fqn === ancestor._creative_type_fqn)?.name || ''}
                  </span>
                </div>
              </div>
              {collapsedGroups.has(ancestorFqn) ? null : creatives.length === 0 ? (
                <div className={styles.emptyGroupPlaceholder}>
                  <p className={styles.emptyGroupText}>
                    No {(currentCreativeType?.plural_name || currentCreativeType?.name || 'items').toLowerCase()} derived from this {(sparkCreativeTypes.find(ct => ct.fqn === ancestor._creative_type_fqn)?.name || '').toLowerCase()} yet
                  </p>
                  {(() => {
                    const deriveAction = ancestorDeriveActions[ancestor._creative_type_fqn];
                    if (!deriveAction || isClosed) return null;
                    return (
                      <button
                        type="button"
                        className={`${styles.emptyStatePrimaryButton} ${styles.aiButton}`}
                        onClick={() => onTriggerAction?.(deriveAction, [ancestorFqn])}
                      >
                        <AutoAwesomeIcon fontSize="small" />
                        {deriveAction.formatted_label}
                      </button>
                    );
                  })()}
                </div>
              ) : viewMode === 'list' ? (
                <div className={styles.listContainer}>
                  {creatives.map((creative) => (
                    <CreativeListItem
                      key={creative.fqn}
                      fqn={creative.fqn}
                      title={creative.name}
                      description={creative.description || ''}
                      thumbnailUrl={creative._hero_image_id ? atlasClient.getAssetUrl(creative._hero_image_id) : undefined}
                      tagPattern={(creative as any).tag_pattern || []}
                      labels={creative._labels || []}
                      isPublished={creative.is_published}
                      onClick={() => handleCreativeClick(creative.fqn)}
                    />
                  ))}
                </div>
              ) : (
                <ul className={`${styles.creativesContainer} ${styles.largeGrid}`}>
                  {creatives.map(creative => (
                    <li key={creative.fqn} className={styles.cardWrapper}>
                      <CreativeCard
                        creative={creative}
                        size="large"
                        onClick={() => handleCreativeClick(creative.fqn)}
                        onDelete={onDeleteCreatives && !isClosed && !(creative as any).is_published ?
                          (fqn) => {
                            onDeleteCreatives([fqn]);
                          } : undefined}
                        thumbnailUrl={undefined}
                        activeIterationNumber={creative._iteration_fqns?.length}
                        isProcessing={isCreativeProcessing(creative)}
                        jobStatusTone={getCreativeJobStatusTone(creative)}
                        hasUnseen={unseenStatuses[creative.fqn]}
                        heroRefreshCounter={dataRefreshCounter}
                        availableLabels={allLabels}
                        onAddLabel={!isClosed ? handleCardAddLabel : undefined}
                        onRemoveLabel={!isClosed ? handleCardRemoveLabel : undefined}
                        isReadOnly={isClosed}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Expose action handlers and state to parent via ref
  useImperativeHandle(ref, () => ({
    applicableActions,
    selectedCreativeFQNs: [],
    hasPublishedSelected: false,
    handleActionMenuItemClick: (action: ResolvedSparkAction) => {
      if (onTriggerAction) {
        onTriggerAction(action, []);
      }
    },
    handleDeleteClick: () => {
      // No-op without multi-select; individual cards handle their own deletion
    },
  }), [applicableActions, onTriggerAction]);

  return (
    <div className={styles.board}>
      {/* Header Controls Row */}
      <div className={styles.headerBar}>
        <div className={styles.headerControls}>
          <div className={styles.controlsLeft}>
            {/* Filters Toggle Button */}
            <button
              type="button"
              className={`${styles.filtersToggleButton} ${isFiltersPanelOpen ? styles.filtersToggleActive : ''} ${totalActiveFiltersCount > 0 ? styles.filtersHasActive : ''}`}
              onClick={() => setIsFiltersPanelOpen(prev => !prev)}
              title="Toggle filters panel"
            >
              <TuneIcon fontSize="small" />
              <span>Filters</span>
              {totalActiveFiltersCount > 0 && (
                <span className={styles.filtersCountBadge}>{totalActiveFiltersCount}</span>
              )}
              <KeyboardArrowDownIcon
                fontSize="small"
                className={`${styles.dropdownArrow} ${isFiltersPanelOpen ? styles.open : ''}`}
              />
            </button>
          </div>

          <div className={styles.controlsRight}>
            {/* View Mode Toggle - identical to search toolbar */}
            <div className={styles.viewControl}>
              <div className={styles.viewModeToggle}>
                <button
                  className={`${styles.viewModeButton} ${viewMode === 'grid' ? styles.active : ''}`}
                  onClick={() => handleViewModeToggle('grid')}
                  title="Grid view"
                >
                  <GridViewIcon fontSize="small" />
                </button>
                <button
                  className={`${styles.viewModeButton} ${viewMode === 'list' ? styles.active : ''}`}
                  onClick={() => handleViewModeToggle('list')}
                  title="List view"
                >
                  <ViewStreamIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Filters Panel */}
        {isFiltersPanelOpen && (
          <div className={styles.filtersPanel}>
            {/* Sort Control */}
            <div className={styles.filterControl} ref={sortDropdownRef}>
              <div
                className={styles.filterTrigger}
                onClick={() => setIsSortDropdownOpen(open => !open)}
              >
                <SortIcon fontSize="small" />
                <div className={styles.filterLabelDisplay}>
                  {{
                    'name-asc': 'Name (A-Z)',
                    'name-desc': 'Name (Z-A)',
                    'createdAt-desc': 'Created (newest)',
                    'createdAt-asc': 'Created (oldest)',
                    'updatedAt-desc': 'Updated (newest)',
                    'updatedAt-asc': 'Updated (oldest)',
                  }[`${sortConfig.field}-${sortConfig.order}`] || 'Sort'}
                </div>
                <KeyboardArrowDownIcon
                  fontSize="small"
                  className={`${styles.dropdownArrow} ${isSortDropdownOpen ? styles.open : ''}`}
                />
              </div>

              {isSortDropdownOpen && (
                <div className={styles.labelDropdown}>
                  <ul className={styles.labelsList}>
                    {([
                      { value: 'name-asc', label: 'Name (A-Z)' },
                      { value: 'name-desc', label: 'Name (Z-A)' },
                      { value: 'createdAt-desc', label: 'Created (newest first)' },
                      { value: 'createdAt-asc', label: 'Created (oldest first)' },
                      { value: 'updatedAt-desc', label: 'Updated (newest first)' },
                      { value: 'updatedAt-asc', label: 'Updated (oldest first)' },
                    ] as { value: string; label: string }[]).map(opt => (
                      <li key={opt.value} className={styles.labelItem}>
                        <div
                          className={`${styles.filterItem} ${`${sortConfig.field}-${sortConfig.order}` === opt.value ? styles.active : ''}`}
                          onClick={() => {
                            const [field, order] = opt.value.split('-') as [SortField, SortOrder];
                            setSortConfig({ field, order });
                            setIsSortDropdownOpen(false);
                          }}
                        >
                          <span className={styles.labelText}>{opt.label}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Filter/Label Control */}
            <div className={styles.filterControl} ref={labelDropdownRef}>
              <div
                className={styles.filterTrigger}
                onClick={handleFilterDropdownToggle}
              >
                <LabelIcon fontSize="small" />
                <div className={styles.filterLabelDisplay}>
                  Filter Labels
                  {filterLabel && (
                    <span className={styles.activeFilterBadge}>
                      <LabelIcon fontSize="inherit" className={styles.labelIcon} />
                      {filterLabel}
                    </span>
                  )}
                </div>
                <KeyboardArrowDownIcon
                  fontSize="small"
                  className={`${styles.dropdownArrow} ${isLabelDropdownOpen ? styles.open : ''}`}
                />
              </div>

              {isLabelDropdownOpen && (
                <div className={styles.labelDropdown}>
                  {allLabels.length === 0 ? (
                    <div className={styles.emptyLabelsMessage}>
                      No labels defined yet
                    </div>
                  ) : (
                    <ul className={styles.labelsList}>
                      {/* Clear filter option */}
                      {filterLabel && (
                        <li className={`${styles.labelItem} ${styles.clearFilterItem}`}>
                          <div
                            className={styles.clearFilter}
                            onClick={handleClearFilter}
                          >
                            <ClearIcon fontSize="small" className={styles.clearIcon} />
                            <span className={styles.clearFilterText}>Clear filter</span>
                          </div>
                        </li>
                      )}

                      {allLabels.map(label => (
                        <li key={label} className={styles.labelItem}>
                          <div
                            className={`${styles.filterItem} ${filterLabel === label ? styles.active : ''}`}
                            onClick={() => handleFilterSelect(label)}
                          >
                            {filterLabel === label ? (
                              <RadioButtonCheckedIcon className={styles.radioIcon} fontSize="small" />
                            ) : (
                              <RadioButtonUncheckedIcon className={styles.radioIcon} fontSize="small" />
                            )}
                            <span className={styles.labelText}>{label}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Tag Filter Control */}
            {hasTags && (
              <div className={styles.filterControl} ref={tagFilterRef}>
                <div
                  className={styles.filterTrigger}
                  onClick={handleTagFilterDropdownToggle}
                >
                  <FilterAltIcon fontSize="small" />
                  <div className={styles.filterLabelDisplay}>
                    Filter Tags
                    {tagFilterActiveCount > 0 && (
                      <span className={styles.activeFilterBadge}>
                        {tagFilterActiveCount}
                      </span>
                    )}
                  </div>
                  <KeyboardArrowDownIcon
                    fontSize="small"
                    className={`${styles.dropdownArrow} ${isTagFilterOpen ? styles.open : ''}`}
                  />
                </div>

                {isTagFilterOpen && (
                  <div className={styles.labelDropdown}>
                    {tagFilterDimensions.length === 0 ? (
                      <div className={styles.emptyLabelsMessage}>
                        No tags available
                      </div>
                    ) : (
                      <ul className={styles.labelsList}>
                        {tagFilterActiveCount > 0 && (
                          <li className={`${styles.labelItem} ${styles.clearFilterItem}`}>
                            <div
                              className={styles.clearFilter}
                              onClick={handleClearTagFilter}
                            >
                              <ClearIcon fontSize="small" className={styles.clearIcon} />
                              <span className={styles.clearFilterText}>Clear filter</span>
                            </div>
                          </li>
                        )}
                        {tagFilterDimensions.map((dimension) => (
                          <li key={dimension.dimensionFqn} className={styles.labelItem}>
                            <div className={`${styles.labelText} ${styles.dropdownGroupHeader}`}>
                              {dimension.name}
                            </div>
                            {dimension.elements.map((el) => {
                              const checked = _selectedElementsByDimension.get(dimension.dimensionFqn)?.has(el.fqn) ?? false;
                              return (
                                <div key={el.fqn} className={styles.filterItem}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => handleToggleTagFilterElement(dimension.dimensionFqn, el.fqn)}
                                    />
                                    <span className={styles.labelText}>{el.name}</span>
                                  </label>
                                </div>
                              );
                            })}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Unseen Only Toggle */}
            <button
              type="button"
              className={`${styles.unseenControl} ${unseenOnly ? styles.unseenControlActive : ''}`}
              onClick={() => setUnseenOnly(prev => !prev)}
              title="Show only unseen creatives"
            >
              <FilterAltIcon fontSize="small" />
              <span className={styles.unseenText}>Unseen Only</span>
            </button>

          </div>
        )}
      </div>
      
      {/* Active Jobs Section */}
      {relevantJobEntries.length > 0 && (
        <div className={styles.activeJobsSection}>
          <ul className={styles.activeJobsList}>
            {relevantJobEntries.map(({ job, navigation, isCompletionCard, title, summary }) => (
              <li key={job.fqn} className={styles.activeJobListItem}>
                <ActiveJobCard
                  job={job}
                  onClick={(j) => setSelectedJobFqnForDetail(j.fqn)}
                  completionTitle={isCompletionCard ? title : undefined}
                  completionSummary={isCompletionCard ? summary : undefined}
                  resultNavigation={isCompletionCard && navigation ? {
                    ...navigation,
                    action: async () => {
                      setLocallyDismissedCompletionJobFqns((prev) => {
                        const next = new Set(prev);
                        next.add(job.fqn);
                        return next;
                      });
                      await markSparkJobAsSeen([job.fqn]);
                      await navigation.action();
                    },
                  } : null}
                  onDismissCompletion={isCompletionCard ? async (dismissedJob) => {
                    setLocallyDismissedCompletionJobFqns((prev) => {
                      const next = new Set(prev);
                      next.add(dismissedJob.fqn);
                      return next;
                    });
                    await markSparkJobAsSeen([dismissedJob.fqn]);
                  } : undefined}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Creatives Grid */}
      <div ref={creativesGridRef} className={styles.resultsRegion}>
        {renderCreativeCards()}
      </div>

      {/* Label Deletion confirmation modal */}
      <ConfirmationModal
        isOpen={isDeleteLabelModalOpen}
        onClose={() => setIsDeleteLabelModalOpen(false)}
        onConfirm={handleDeleteLabelConfirm}
        title="Delete label"
        message={`Are you sure you want to delete the label "${labelToDelete}"?\nThis will remove it from all creatives.`}
        confirmText="Delete"
        cancelText="Cancel"
        isConfirming={isDeletingLabel}
        variant="destructive"
      />

      {/* Legacy Publish/Unpublish confirmation modal removed */}

      {/* Job Detail Modal */}
      <JobDetailModal
        isOpen={!!selectedJobFqnForDetail}
        jobFqn={selectedJobFqnForDetail}
        onClose={() => setSelectedJobFqnForDetail(null)}
      />
    </div>
  );
});

export default CreativeBoard;
